import { useContext, useEffect } from "react";
import { Socket } from "socket.io-client";
import IOnGetPOShipmentDTO from "../../models/DTO/IOnGetPOShipmentDTO";
import IOnFinalizePOShipmentDTO from "../../models/DTO/IOnFinalizePOShipmentDTO";
import IOnProcessPOShipmentItemDTO from "../../models/DTO/IOnProcessPOShipmentItemDTO";
import EndpointsEnum from "../../models/EndpointsEnum";
import IPO_Shipment from "../../models/IPO_Shipment";
import IPO_Shipment_Catalog_Item_MapUI from "../../models/IPO_Shipment_Catalog_Item_MapUI";
import { SocketContext } from "@contexts/SocketContext";
import UiContext from "@contexts/UiContext";
import { buildOrderReservationNotification } from "@utils/poReceiveOrderReservation";

interface UsePurchaseOrderShipmentSocketProps {
    setPoShipment: (shipment: IPO_Shipment | ((prev: IPO_Shipment | undefined) => IPO_Shipment | undefined)) => void;
    setPoShipmentItems: (
        items:
            | IPO_Shipment_Catalog_Item_MapUI[]
            | ((prev: IPO_Shipment_Catalog_Item_MapUI[] | undefined) => IPO_Shipment_Catalog_Item_MapUI[] | undefined)
    ) => void;
    setDataFetch: (value: boolean) => void;
    setInitialData: (value: boolean) => void;
    setIsFormDirty: (value: boolean) => void;
}

export const usePurchaseOrderShipmentSocket = ({
    setPoShipment,
    setPoShipmentItems,
    setDataFetch,
    setInitialData,
    setIsFormDirty,
}: UsePurchaseOrderShipmentSocketProps) => {
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    useEffect(() => {
        const onGetPOShipment = (data: IOnGetPOShipmentDTO) => {
            const poShipmentItems: IPO_Shipment_Catalog_Item_MapUI[] = data.poShipment!.PO_Shipment_Catalog_Item_Maps!.map((itemMap) => {
                return {
                    ...itemMap,
                    isDirty: false,
                };
            });

            setPoShipment(data.poShipment);
            setPoShipmentItems(poShipmentItems);
            setDataFetch(false);
            setInitialData(false);
        };

        const onFinalizePOShipment = (data: IOnFinalizePOShipmentDTO) => {
            setPoShipment((prev) => (prev ? { ...prev, is_processed: true } : prev));

            // PO linked to a single sales order: tell the user what was reserved for it on receipt.
            const reservationNotification = buildOrderReservationNotification(data.orderReservation);

            if (reservationNotification) {
                uiContext.notifyFn(null, reservationNotification);
            }
        };

        const onProcessPOShipmentItem = (data: IOnProcessPOShipmentItemDTO) => {
            setPoShipmentItems((prevItems) => {
                if (!prevItems) return prevItems;

                const updatedItems = prevItems.map((item) => {
                    if (item.catalog_item_id === data.PO_Shipment_Item.catalog_item_id) {
                        return { ...item, isDirty: false };
                    }
                    return item;
                });

                const anyDirty = updatedItems.some((item) => item.isDirty);
                setIsFormDirty(anyDirty);

                return updatedItems;
            });
        };

        socket.on(EndpointsEnum.PurchaseOrderOnGetPOShipment, onGetPOShipment);
        socket.on(EndpointsEnum.PurchaseOrderOnFinalizePOShipment, onFinalizePOShipment);
        socket.on(EndpointsEnum.PurchaseOrderOnProcessPOShipmentItem, onProcessPOShipmentItem);

        return () => {
            socket.off(EndpointsEnum.PurchaseOrderOnGetPOShipment, onGetPOShipment);
            socket.off(EndpointsEnum.PurchaseOrderOnFinalizePOShipment, onFinalizePOShipment);
            socket.off(EndpointsEnum.PurchaseOrderOnProcessPOShipmentItem, onProcessPOShipmentItem);
        };
    }, [socket, uiContext, setPoShipment, setPoShipmentItems, setDataFetch, setInitialData, setIsFormDirty]);
};
